import { clsx } from '@alexis/helpers/clsx';
import React, { useCallback, useState } from 'react';

import type { Translations } from '@wegoTypes/translations';

import { useCopyToClipboard } from '../hooks/useCopyToClipboard';
import CopyIcon from '../icons/copy.svg';
import PromoPercentIcon from '../icons/promo_percent.svg';
import styles from '../styles/components/Promo.module.scss';

interface PromoProps {
  className?: string;
  promoCode: string;
  promoDescription: string;
  promoLabel: string;
  translations: Translations;
}

const Promo: React.FC<PromoProps> = ({
  className,
  promoCode,
  promoLabel,
  promoDescription,
  translations,
}) => {
  const [isPromoExpanded, setIsPromoExpanded] = useState<boolean>(false);

  const [isCopied, copy] = useCopyToClipboard();

  const onTogglePromo = useCallback<() => void>(() => {
    setIsPromoExpanded(!isPromoExpanded);
  }, [isPromoExpanded]);

  const onCopiedPromoCode = useCallback<(event: React.MouseEvent<HTMLDivElement>) => void>(
    (event) => {
      event.stopPropagation();
      copy(promoCode);
    },
    [],
  );

  return (
    <div className={clsx(styles.promoContainer, className)}>
      {/* Promo Header (Toggle)*/}
      <div
        className={clsx(styles.promoHeader, !!promoDescription && styles.showToggle)}
        onClick={onTogglePromo}
      >
        <div className={styles.promoLabelContainer}>
          <PromoPercentIcon className={styles.promoBadge} />
          <div className={styles.name}>{promoLabel}</div>
          <i className={clsx(styles.chevron, isPromoExpanded ? styles.top : styles.bottom)}></i>
        </div>

        {!!promoCode ? (
          <div
            className={clsx(styles.promoCodeContainer, isCopied && styles.copied)}
            onClick={onCopiedPromoCode}
          >
            <div className={styles.code}>{promoCode}</div>

            <div className={styles.copyContainer}>
              <CopyIcon className={styles.copyIcon} />
            </div>

            {isCopied && <div className={styles.copiedLabel}>{translations.copied}</div>}
          </div>
        ) : null}
      </div>

      {/* Promo Detail (Dropdown Content) */}
      {!!promoDescription ? (
        <div
          className={clsx(styles.promoDropdownContainer, isPromoExpanded && styles.expand)}
          dangerouslySetInnerHTML={{ __html: promoDescription }}
        ></div>
      ) : null}
    </div>
  );
};

export default Promo;
